import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  TextField,
  Grid,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableContainer,
  TableBody,
  TableCell,
  Typography,
  Box,
  withStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { Modal } from "antd";
import GetAppIcon from "@material-ui/icons/GetApp";
import DateRangeIcon from "@material-ui/icons/DateRange";
import AssessmentIcon from "@material-ui/icons/Assessment";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import HttpService from "../services/httpService";
import { Link } from "react-router-dom";

import * as XLSX from "xlsx";
import MyProjectManagementCapexOverHead from "./MyProjectManagementCapexOverHead";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    // color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
    fontSize: "1.5rem",
    textAlign: "center",
  },
  filterContainer: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: "#fff",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  dateField: {
    marginRight: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  exportButton: {
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.success.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  summaryCard: {
    height: "100%",
    backgroundColor: "#fff",
    transition: "transform 0.2s",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minHeight: "160px",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    },
  },
  summaryIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },
  summaryValue: {
    fontSize: "2rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  summaryLabel: {
    fontSize: "1.1rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    backgroundColor: "#fff",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  table: {
    minWidth: 700,
  },
  tableHeader: {
    backgroundColor: '#1C375D',
    "& .MuiTableCell-head": {
      color: "#fff",
      fontWeight: 500,
    },
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  cellHighlight: {
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
  headerIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1.2rem",
    verticalAlign: "middle",
  },
  headerTotal: {
    display: "block",
    fontSize: "0.875rem",
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
  },
  headerCell: {
    textAlign: "center",
  },
  submitButton: {
    backgroundColor: "#F47E46 !important", // Decorpot Orange
    color: "white",
    padding: '8px 20px',
    "&:hover": { backgroundColor: "#E0663D !important" }, // Darker Orange on hover
  },
  modalTable: {
    minWidth: 600,
  },
}));

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#1C375D",
    color: '#FFF2EB',
    fontWeight: "500",
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const MyProjectManagementDashboard = (props) => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState([]);
  const [processedData, setProcessedData] = useState({});
  const [selectedDateType, setSelectedDateType] = useState('Creation Date');
  const [openModal, setOpenModal] = useState(false);
  const [openPOModal, setOpenPOModal] = useState(false);
  const [countModalData, setCountModalData] = useState([]);
  const [procurementType, setProcurementType] = useState('');
  const [totals, setTotals] = useState({
    byType: {},
    grandTotals: {},
    overallTotal: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    props.setLoading(true);
    props.setSnackInfo("Data Loading please wait...", "info");
    try {
      const response = await HttpService.getProjectManagementData({
        startDate,
        endDate,
        selectedDateType
      });

      if (response?.data?.success) {
        setData(response?.data?.data);
        setTotals(response?.data?.totals);
        processData(response?.data?.data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      props.setSnackInfo({
        open: true,
        message: "Error fetching project data",
        variant: "error",
      });
      // Reset states in case of error
      setData([]);
      setProcessedData({});
      setTotals({
        byType: {},
        grandTotals: {},
        overallTotal: 0,
      });
    } 
    props.setLoading(false);
  };

  const processData = (rawData) => {
    if (!Array.isArray(rawData)) {
      console.error("Invalid data format received");
      return;
    }

    const processed = {
      Customer: {
        label: "Customer Project",
        PO: { amount: 0, count: 0, byStatus: {} },
        SO: { amount: 0, count: 0, byStatus: {} },
        MTR: { amount: 0, count: 0, byStatus: {} },
      },
      // Overhead: {
      //   label: "Overhead Project",
      //   PO: { amount: 0, count: 0, byStatus: {} },
      //   SO: { amount: 0, count: 0, byStatus: {} },
      //   MTR: { amount: 0, count: 0, byStatus: {} },
      // },
      // Capex: {
      //   label: "Capex Project",
      //   PO: { amount: 0, count: 0, byStatus: {} },
      //   SO: { amount: 0, count: 0, byStatus: {} },
      //   MTR: { amount: 0, count: 0, byStatus: {} },
      // },
    };

    const typeMap = {
      po: "PO",
      so: "SO",
      mtr: "MTR",
    };

    rawData.forEach((item) => {
      try {
        const projectType = item.project_type;
        if (!projectType || !processed[projectType]) {
          console.warn(
            `Skipping item with invalid project type: ${projectType}`
          );
          return;
        }

        const transactionType = typeMap[item?.type?.toLowerCase()];
        if (!transactionType || !processed[projectType][transactionType]) {
          console.warn(
            `Skipping item with invalid transaction type: ${item?.type}`
          );
          return;
        }

        const status = item.status || "Unknown";
        const amount = Number(item?.amount || 0);

        processed[projectType][transactionType].amount += amount;
        processed[projectType][transactionType].count += 1;

        if (!processed[projectType][transactionType].byStatus[status]) {
          processed[projectType][transactionType].byStatus[status] = {
            amount: 0,
            count: 0,
          };
        }
        processed[projectType][transactionType].byStatus[status].amount +=
          amount;
        processed[projectType][transactionType].byStatus[status].count += 1;
      } catch (error) {
        console.error("Error processing item:", item, error);
      }
    });

    setProcessedData(processed);
  };

  const handleExportToExcel = () => {
    const exportData = data.map((item) => ({
      "Project No.": item?.project_no,
      "Project Type":
        item?.project_type?.charAt(0).toUpperCase() +
        item?.project_type?.slice(1) +
        " Project",
      "PO/SO/MTR ID": item?.orderId,
      "Amount": item?.amount,
      "PO/SO/MTR Status": item?.status,
      "PO/SO/MTR Created Date": moment(item?.creation_date).format("DD-MM-YYYY"),
      "Project Status": item?.project_type_value === 0 ? item?.lead_current_stage : (item?.project_current_stage || 'NA'),
      "Project Created Date": item?.projectStartDate ? moment(item?.projectStartDate).format("DD-MM-YYYY") : "NA",
      "Project Completion Date": item?.projectCompletionDate ? moment(item?.projectCompletionDate).format("DD-MM-YYYY") : "NA"
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Project Data");
    XLSX.writeFile(wb, "project_management_cx_data.xlsx");
  };

  const calculateTotals = () => {
    return {
      totalAmount: totals?.overallTotal || 0,
      totalCount: data?.length || 0,
      poAmount: totals?.grandTotals?.procurement || 0,
      soAmount: totals?.grandTotals?.serviceOrder || 0,
      mtrAmount: totals?.grandTotals?.materialTransfer || 0,
    };
  };


  // handle submit startDate and end Date
  const handleSubmit = ()=>{
    fetchData();
  }

  // Count PO/SO/MTR Data
  const handleCountData = (key) => {
    setProcurementType(key);
    const allData = data.filter(item => item.orderId?.startsWith(key));
    setOpenPOModal(true);
    setCountModalData(allData);
  };
  

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Project Management Dashboard
        </Typography>
      </div>
    {/* Customer Project */}
      <Paper className={classes.filterContainer}>
        <Grid container spacing={3} alignItems="center">

          <Grid item xs={12} sm={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="date-filter">Select Filter Type</InputLabel>
              <Select
                labelId="date-filter"
                id="date-filter"
                value={selectedDateType}
                label="Select Filter Type"
                variant="outlined"
                onChange={(e) => setSelectedDateType(e.target.value)}
              >
                <MenuItem value={'Creation Date'}>Creation Date</MenuItem>
                <MenuItem value={'Completion Date'}>Completion Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={classes.dateField}
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: <DateRangeIcon color="action" />,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={classes.dateField}
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: <DateRangeIcon color="action" />,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                className={classes.submitButton}
              >
                Submit
              </Button>
          </Grid>

          <Grid item xs={12} sm={1}>
            <Tooltip title="Export to Excel">
              <Button
                variant="contained"
                className={classes.exportButton}
                onClick={handleExportToExcel}
                startIcon={<GetAppIcon />}
                fullWidth
              >
                Export Report
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell component="th" rowSpan={2}>Project Type</TableCell>
              <TableCell component="th" colSpan={3} className={classes.headerCell}>
                <Box>
                  <span>
                    <AccountBalanceWalletIcon className={classes.headerIcon} />
                    PO
                  </span>
                  <Typography component="span" className={classes.headerTotal}>
                    Total: ₹{calculateTotals().poAmount.toLocaleString()}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell component="th" colSpan={3} className={classes.headerCell}>
                <Box>
                  <span>
                    <AccountBalanceWalletIcon className={classes.headerIcon} />
                    SO
                  </span>
                  <Typography component="span" className={classes.headerTotal}>
                    Total: ₹{calculateTotals().soAmount.toLocaleString()}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell component="th" colSpan={3} className={classes.headerCell}>
                <Box>
                  <span>
                    <AccountBalanceWalletIcon className={classes.headerIcon} />
                    MTR
                  </span>
                  <Typography component="span" className={classes.headerTotal}>
                    Total: ₹{calculateTotals().mtrAmount.toLocaleString()}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow className={classes.tableHeader}>
              <TableCell align="center">Amount</TableCell>
              <TableCell align="center">Count</TableCell>
              <TableCell align="center">Status Breakdown</TableCell>
              <TableCell align="center">Amount</TableCell>
              <TableCell align="center">Count</TableCell>
              <TableCell align="center">Status Breakdown</TableCell>
              <TableCell align="center">Amount</TableCell>
              <TableCell align="center">Count</TableCell>
              <TableCell align="center">Status Breakdown</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(processedData)?.map(([key, row]) => (
              <TableRow key={key} className={classes.tableRow}>
                <TableCell
                  component="th"
                  scope="row"
                  className={classes.cellHighlight}
                  onClick={()=>setOpenModal(true)}
                  style={{ cursor: 'pointer' }} 
                 >
                  {row?.label}
                </TableCell>
                <TableCell component="th" align="center">
                  ₹{row?.PO?.amount?.toLocaleString()}
                </TableCell>
                <TableCell component="th" align="center" onClick={()=>handleCountData('PO')} style={{ cursor: 'pointer', textDecoration: 'underline' }} >{row?.PO?.count}</TableCell>
                <TableCell component="th" align="right">
                { Object.entries(row?.PO?.byStatus || {}).length > 0
                  ? Object.entries(row.PO.byStatus).map(([status, data]) => (
                      <div key={status}>
                        {status}: ₹{data.amount?.toLocaleString()} ({data.count})
                      </div>
                    ))
                  : '0'}
              </TableCell>
                <TableCell component="th" align="center">
                  ₹{row?.SO?.amount?.toLocaleString()}
                </TableCell>
                <TableCell component="th" align="center" onClick={()=>handleCountData('SO')} style={{ cursor: 'pointer', textDecoration: 'underline' }} >{row?.SO?.count}</TableCell>
                <TableCell component="th" align="right">
                { Object.entries(row?.SO?.byStatus || {}).length > 0 
                  ? Object.entries(row?.SO?.byStatus)?.map(([status, data]) => (
                    <div key={status}>
                      {status}: ₹{data?.amount?.toLocaleString()} ({data?.count})
                    </div>
                  )): '0'}
                </TableCell>
                <TableCell component="th" align="center">
                  ₹{row.MTR.amount.toLocaleString()}
                </TableCell>
                <TableCell component="th" align="center" onClick={()=>handleCountData('MTR')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{row?.MTR?.count}</TableCell>
                <TableCell component="th" align="right">
                 {Object.entries(row?.MTR?.byStatus || {}).length > 0
                  ? Object.entries(row?.MTR?.byStatus).map(([status, data]) => (
                    <div key={status}>
                      {status}: ₹{data?.amount?.toLocaleString()} ({data?.count})
                    </div>
                  )): '0'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    {/* Modal */}
      <Modal
        title="Customer Project"
        centered
        visible={openModal}
        onCancel={() => setOpenModal(false)}
        width={1000}
        maskClosable={true}
        footer={null}
      >
        <TableContainer className={classes.modalTable}>
          <Table
            className={classes.modalTable}
            stickyHeader
            aria-label="sticky table"
          >
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Sl. No.</StyledTableCell>
                <StyledTableCell>Project No.</StyledTableCell>
                <StyledTableCell>Project Type</StyledTableCell>
                <StyledTableCell>PO/SO/MTR ID</StyledTableCell>
                <StyledTableCell>Amount</StyledTableCell>
                <StyledTableCell>PO/SO/MTR Status</StyledTableCell>
                <StyledTableCell>PO/SO/MTR Created Date</StyledTableCell>
                <StyledTableCell>Project Status</StyledTableCell>
                <StyledTableCell>Project Created Date (Design Sign-off)</StyledTableCell>
                <StyledTableCell>Project Completion Date</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {data &&
                data.map((data, i) => {
                  return (
                    <>
                      <StyledTableRow key={i}>
                        <StyledTableCell component="th" style={{ textAlign: "center" }}>{i + 1}</StyledTableCell>
                        <StyledTableCell
                          component="th"
                          style={{ textAlign: "center" }}
                        >
                          {data?.project_no}
                        </StyledTableCell>
                        <StyledTableCell
                          component="th"
                          style={{ textAlign: "center" }}
                        >
                          {data?.project_type}
                        </StyledTableCell>
                        <StyledTableCell
                          component="th"
                          style={{ textAlign: "center" }}
                        >
                          {data?.orderId}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {data?.amount ?  data?.amount : 0}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {data?.status}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {moment(data?.creation_date).format("DD-MM-YYYY")}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {data?.project_type_value === 0 ? data?.lead_current_stage : (data?.project_current_stage || 'NA')}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {data?.projectStartDate? moment(data?.projectStartDate).format("DD-MM-YYYY") : 'NA'}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          { data?.projectCompletionDate ? moment(data?.projectCompletionDate).format("DD-MM-YYYY") : "NA"}
                        </StyledTableCell>
                      </StyledTableRow>
                    </>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Modal>

      {/* count modal */}
      <Modal
        title={`${procurementType} Data`}
        centered
        visible={openPOModal}
        onCancel={() => setOpenPOModal(false)}
        width={1000}
        maskClosable={true}
        footer={null}
      >
        <TableContainer className={classes.modalTable}>
          <Table
            className={classes.modalTable}
            stickyHeader
            aria-label="sticky table"
          >
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Sl. No.</StyledTableCell>
                <StyledTableCell>Project No.</StyledTableCell>
                <StyledTableCell>Project Type</StyledTableCell>
                <StyledTableCell>ID</StyledTableCell>
                <StyledTableCell>Amount</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Created Date</StyledTableCell>
                <StyledTableCell>Project Status</StyledTableCell>
                <StyledTableCell>Project Created Date (Design Sign-off)</StyledTableCell>
                <StyledTableCell>Project Completion Date</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {countModalData &&
                countModalData.map((data, i) => {
                  return (
                    <>
                      <StyledTableRow key={i}>
                        <StyledTableCell component="th" style={{ textAlign: "center" }}>{i + 1}</StyledTableCell>
                        <StyledTableCell
                          component="th"
                          style={{ textAlign: "center" }}
                        >
                          {data?.project_no}
                        </StyledTableCell>
                        <StyledTableCell
                          component="th"
                          style={{ textAlign: "center" }}
                        >
                          {data?.project_type}
                        </StyledTableCell>
                        <StyledTableCell
                          component="th"
                          style={{ textAlign: "center" }}
                        >
                          {data?.orderId}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {data?.amount ?  data?.amount : 0}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {data?.status}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {moment(data?.creation_date).format("DD-MM-YYYY")}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {data?.project_type_value === 0 ? data?.lead_current_stage : (data?.project_current_stage || 'NA')}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          {data?.projectStartDate? moment(data?.projectStartDate).format("DD-MM-YYYY") : 'NA'}
                        </StyledTableCell>
                        <StyledTableCell component="th">
                          { data?.projectCompletionDate ? moment(data?.projectCompletionDate).format("DD-MM-YYYY") : "NA"}
                        </StyledTableCell>
                      </StyledTableRow>
                    </>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Modal>

      {/* capex and overhead project data added in this component rendering */}
      <MyProjectManagementCapexOverHead/>
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(
  MyProjectManagementDashboard
);